import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Row, Col, Card, CardBody, Input, Pagination, PaginationItem, PaginationLink, Badge, Label } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const PaymentHistory = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [payments, setPayments] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_records: 0 });

    const [filters, setFilters] = useState({
        purchase_id: "",
        payment_method: null, // React-select uses object
        from_date: null, // MUI initialized as null
        to_date: null,
        page: 1,
        limit: 10
    });

    const methodOptions = [
        { value: "cash", label: "Cash" },
        { value: "upi", label: "UPI" },
        { value: "bank", label: "Bank" }
    ];

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '45px',
            height: '45px',
            borderRadius: '8px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: '#2e4a3d' }
        }),
        valueContainer: (provided) => ({ ...provided, height: '45px', padding: '0 6px' }),
        input: (provided) => ({ ...provided, margin: '0px' }),
        indicatorSeparator: () => ({ display: 'none' }),
        indicatorsContainer: (provided) => ({ ...provided, height: '45px' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    const loadPayments = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                ...filters,
                payment_method: filters.payment_method ? filters.payment_method.value : "",
                from_date: filters.from_date ? dayjs(filters.from_date).format('YYYY-MM-DD') : "",
                to_date: filters.to_date ? dayjs(filters.to_date).format('YYYY-MM-DD') : ""
            };
            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({
                method: "GET",
                url: `/purchase_payments/list.php?${queryParams}`
            });

            if (res?.success) {
                setPayments(res.data || []);
                setAnalytics(res.analytics || null);

                // 🔥 FIXED: Safely extracting pagination data exactly as the PHP API sends it
                setPagination({
                    current_page: res?.current_page || 1,
                    total_pages: res?.total_pages || 1,
                    total_records: res?.total_records || 0
                });

            } else {
                setPayments([]);
                toast.error(res?.message || "Failed to fetch payments");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => { loadPayments(); }, [filters.page]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold text-theme m-0">Payment Transactions</h3>
                        <p className="text-muted small m-0">Track all outward payments to farmers and traders</p>
                    </div>
                    <Button color="success" className="rounded-pill px-4 shadow-sm" onClick={() => navigate("/purchases/pay")}>
                        <i className="bx bx-plus me-1"></i> New Payment
                    </Button>
                </div>

                {analytics && (
                    <Row className="mb-4 g-3">
                        <Col md={3}>
                            <Card className="border-0 shadow-sm rounded-4 text-center">
                                <CardBody className="p-3">
                                    <p className="text-muted small fw-bold mb-1 text-uppercase">Total Paid</p>
                                    <h4 className="fw-bold mb-0 text-success">₹{analytics.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm rounded-4 text-center">
                                <CardBody className="p-3">
                                    <p className="text-muted small fw-bold mb-1 text-uppercase">Cash</p>
                                    <h4 className="fw-bold mb-0 text-primary">₹{analytics.by_method?.cash?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm rounded-4 text-center">
                                <CardBody className="p-3">
                                    <p className="text-muted small fw-bold mb-1 text-uppercase">UPI</p>
                                    <h4 className="fw-bold mb-0 text-info">₹{analytics.by_method?.upi?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm rounded-4 text-center">
                                <CardBody className="p-3">
                                    <p className="text-muted small fw-bold mb-1 text-uppercase">Bank</p>
                                    <h4 className="fw-bold mb-0 text-warning">₹{analytics.by_method?.bank?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="p-3">
                        <Row className="g-2 align-items-end">
                            <Col md={2}>
                                <Label className="small fw-bold">Purchase ID</Label>
                                <Input style={{ height: '45px' }} name="purchase_id" placeholder="ID #" value={filters.purchase_id} onChange={handleFilterChange} />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">Method</Label>
                                <Select
                                    options={methodOptions}
                                    value={filters.payment_method}
                                    onChange={(opt) => setFilters({ ...filters, payment_method: opt, page: 1 })}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="Select"
                                />
                            </Col>
                            <Col md={3}>
                                <Label className="small fw-bold">From Date</Label>
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    disableFuture
                                    value={filters.from_date}
                                    onChange={(val) => setFilters({ ...filters, from_date: val, page: 1 })}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={3}>
                                <Label className="small fw-bold">To Date</Label>
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    disableFuture
                                    value={filters.to_date}
                                    onChange={(val) => setFilters({ ...filters, to_date: val, page: 1 })}
                                    minDate={filters.from_date}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={2}>
                                <Button color="dark" block style={{ height: '45px' }} className="fw-bold" onClick={loadPayments}>
                                    <i className="bx bx-search-alt me-1"></i> Search
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Payment ID</th>
                                    <th>Date</th>
                                    <th>Purchase ID</th>
                                    <th>Amount</th>
                                    <th>Method</th>
                                    <th>Note</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.length > 0 ? (
                                    payments.map((item) => (
                                        <tr key={item.payment_id}>
                                            <td className="ps-4 fw-bold align-middle">#{item.payment_id}</td>
                                            <td className="align-middle">{dayjs(item.created_at).format('DD/MM/YYYY')}</td>
                                            <td className="align-middle">
                                                <Badge color="light" className="text-dark border">
                                                    Ref: #{item.purchase?.id}
                                                </Badge>
                                            </td>
                                            <td className="align-middle">
                                                <div className="fw-bold text-success">
                                                    ₹{item.payment?.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </div>
                                                {/* 🔥 Accurately displaying the final calculated purchase total from API */}
                                                {/* <small className="text-muted">Total Bill: ₹{item.purchase?.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</small> */}
                                            </td>
                                            <td className="text-uppercase small fw-bold align-middle">
                                                <i className={`bx ${item.payment?.method === 'cash' ? 'bx-money' : 'bx-transfer'} me-1`}></i>
                                                {item.payment?.method}
                                            </td>
                                            <td className="text-muted small align-middle" style={{ maxWidth: '200px' }}>
                                                {item.payment?.note || "-"}
                                            </td>
                                            <td className="align-middle">
                                                <Button color="light" size="sm" className="border shadow-sm rounded-circle px-2" onClick={() => navigate(`/purchases/pay/${item.payment_id}`)}              >
                                                    <i className="bx bx-edit text-success"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5 text-muted">No payment records found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* 🔥 FIXED: Pagination logic properly aligned with the updated state structure */}
                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination size="sm">
                            {/* Previous Button */}
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>

                            {/* Dynamic Page Rendering */}
                            {[...Array(pagination.total_pages)].map((_, i) => {
                                const pageNumber = i + 1;
                                const isCurrent = pageNumber === filters.page;

                                // Show first, last, current, and +/- 1 range
                                const shouldShow =
                                    pageNumber === 1 ||
                                    pageNumber === pagination.total_pages ||
                                    (pageNumber >= filters.page - 1 && pageNumber <= filters.page + 1);

                                if (shouldShow) {
                                    return (
                                        <PaginationItem active={isCurrent} key={i}>
                                            <PaginationLink onClick={() => setFilters({ ...filters, page: pageNumber })}>
                                                {pageNumber}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                } else if (
                                    pageNumber === filters.page - 2 ||
                                    pageNumber === filters.page + 2
                                ) {
                                    // Add ellipsis
                                    return (
                                        <PaginationItem disabled key={i}>
                                            <PaginationLink>...</PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            {/* Next Button */}
                            <PaginationItem disabled={filters.page >= pagination.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default PaymentHistory;